// Platform Admin — 🗺 India Geo Map Visualization
// =================================================
// Choropleth map of school distribution across India using react-leaflet + GeoJSON.
// Color intensity = school count. Click on state → district drill-down panel.
import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GEOJSON_URL = '/data/india_states.geojson';

// Map GeoJSON state names (mixed case, & symbol) → canonical (UPPERCASE, "AND")
const GEOJSON_TO_CANONICAL = {
  'Andaman & Nicobar': 'ANDAMAN AND NICOBAR ISLANDS',
  'Andhra Pradesh': 'ANDHRA PRADESH',
  'Arunachal Pradesh': 'ARUNACHAL PRADESH',
  Assam: 'ASSAM',
  Bihar: 'BIHAR',
  Chandigarh: 'CHANDIGARH',
  Chhattisgarh: 'CHHATTISGARH',
  'Dadra and Nagar Haveli and Daman and Diu': 'DADRA AND NAGAR HAVELI AND DAMAN AND DIU',
  Delhi: 'DELHI',
  Goa: 'GOA',
  Gujarat: 'GUJARAT',
  Haryana: 'HARYANA',
  'Himachal Pradesh': 'HIMACHAL PRADESH',
  'Jammu & Kashmir': 'JAMMU AND KASHMIR',
  Jharkhand: 'JHARKHAND',
  Karnataka: 'KARNATAKA',
  Kerala: 'KERALA',
  Ladakh: 'LADAKH',
  Lakshadweep: 'LAKSHADWEEP',
  'Madhya Pradesh': 'MADHYA PRADESH',
  Maharashtra: 'MAHARASHTRA',
  Manipur: 'MANIPUR',
  Meghalaya: 'MEGHALAYA',
  Mizoram: 'MIZORAM',
  Nagaland: 'NAGALAND',
  Odisha: 'ODISHA',
  Puducherry: 'PUDUCHERRY',
  Punjab: 'PUNJAB',
  Rajasthan: 'RAJASTHAN',
  Sikkim: 'SIKKIM',
  'Tamil Nadu': 'TAMIL NADU',
  Telangana: 'TELANGANA',
  Tripura: 'TRIPURA',
  'Uttar Pradesh': 'UTTAR PRADESH',
  Uttarakhand: 'UTTARAKHAND',
  'West Bengal': 'WEST BENGAL',
};

// Color scale — emerald gradient (light → dark) based on percentile of school count
function colorFor(count, max) {
  if (!count || count <= 0) return '#e2e8f0'; // slate-200
  const pct = count / Math.max(max, 1);
  if (pct >= 0.7) return '#065f46'; // emerald-800
  if (pct >= 0.4) return '#047857'; // emerald-700
  if (pct >= 0.2) return '#10b981'; // emerald-500
  if (pct >= 0.08) return '#34d399'; // emerald-400
  if (pct >= 0.02) return '#a7f3d0'; // emerald-200
  return '#d1fae5'; // emerald-100
}

const LEGEND_BUCKETS = [
  { label: '70%+', color: '#065f46' },
  { label: '40-70%', color: '#047857' },
  { label: '20-40%', color: '#10b981' },
  { label: '8-20%', color: '#34d399' },
  { label: '2-8%', color: '#a7f3d0' },
  { label: '<2%', color: '#d1fae5' },
];

export default function IndiaGeoMapPanel({ token, language = 'hi' }) {
  const [stats, setStats] = useState(null);
  const [geo, setGeo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredState, setHoveredState] = useState(null);
  const [drilldown, setDrilldown] = useState(null); // { state, districts: [...] }
  const [drillLoading, setDrillLoading] = useState(false);

  // Auto-Launch Outreach modal state
  const [outreachState, setOutreachState] = useState(null); // canonical state name
  const [outreachPreview, setOutreachPreview] = useState(null);
  const [outreachVideos, setOutreachVideos] = useState([]);
  const [outreachVideoId, setOutreachVideoId] = useState('');
  const [outreachSchedule, setOutreachSchedule] = useState('best_hour');
  const [outreachLowCoverage, setOutreachLowCoverage] = useState(false);
  const [outreachLaunching, setOutreachLaunching] = useState(false);
  const [outreachResult, setOutreachResult] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/school-finder/geo-map/stats`, { headers }).then((r) => r.json()),
      fetch(GEOJSON_URL).then((r) => r.json()),
    ])
      .then(([statsData, geoData]) => {
        if (statsData.success) setStats(statsData);
        setGeo(geoData);
        setLoading(false);
      })
      .catch((e) => { console.warn('map load:', e); setLoading(false); });
    /* eslint-disable-next-line */
  }, []);

  const statsByState = stats
    ? Object.fromEntries(stats.items.map((it) => [it.state, it]))
    : {};

  const onEachFeature = (feature, layer) => {
    const geoName = feature.properties.ST_NM;
    const canonical = GEOJSON_TO_CANONICAL[geoName] || geoName.toUpperCase();
    const stateInfo = statsByState[canonical];
    const count = stateInfo?.schools || 0;

    layer.on({
      mouseover: (e) => {
        setHoveredState({ name: geoName, canonical, ...stateInfo, schools: count });
        e.target.setStyle({ weight: 2.5, color: '#0f766e', fillOpacity: 0.9 });
      },
      mouseout: (e) => {
        setHoveredState(null);
        e.target.setStyle({ weight: 1, color: '#475569', fillOpacity: 0.75 });
      },
      click: () => loadDrilldown(canonical),
    });
  };

  const styleFor = (feature) => {
    const geoName = feature.properties.ST_NM;
    const canonical = GEOJSON_TO_CANONICAL[geoName] || geoName.toUpperCase();
    const count = statsByState[canonical]?.schools || 0;
    return {
      fillColor: colorFor(count, stats?.max_schools || 1),
      weight: 1,
      color: '#475569',
      fillOpacity: 0.75,
    };
  };

  const loadDrilldown = async (state) => {
    setDrillLoading(true);
    setDrilldown({ state, districts: [] });
    try {
      const r = await fetch(`${API_URL}/api/school-finder/geo-map/state/${encodeURIComponent(state)}?limit=50`, { headers });
      const d = await r.json();
      if (d.success) setDrilldown({ state, districts: d.districts || [], total: d.total_schools, max: d.max_schools });
    } catch (e) { console.warn('drill:', e); }
    setDrillLoading(false);
  };

  const openOutreach = async (state) => {
    setOutreachState(state);
    setOutreachPreview(null);
    setOutreachResult(null);
    setOutreachVideoId('');
    setOutreachSchedule('best_hour');
    setOutreachLowCoverage(false);
    // Fetch preview + videos in parallel
    try {
      const [pr, vr] = await Promise.all([
        fetch(`${API_URL}/api/school-finder/geo-map/outreach/preview?state=${encodeURIComponent(state)}`, { headers }),
        fetch(`${API_URL}/api/veo/jobs?status=completed&limit=20`, { headers }),
      ]);
      const pj = await pr.json(); const vj = await vr.json();
      if (pj.success) setOutreachPreview(pj);
      if (vj.success) setOutreachVideos((vj.items || []).filter((j) => j.status === 'completed'));
    } catch (e) { console.warn('outreach prep:', e); }
  };

  const refreshOutreachPreview = async (lowCoverage) => {
    if (!outreachState) return;
    try {
      const r = await fetch(`${API_URL}/api/school-finder/geo-map/outreach/preview?state=${encodeURIComponent(outreachState)}&only_low_coverage=${lowCoverage}`, { headers });
      const j = await r.json();
      if (j.success) setOutreachPreview(j);
    } catch (e) { console.warn('preview refresh:', e); }
  };

  const launchOutreach = async () => {
    if (!outreachState) return;
    setOutreachLaunching(true);
    setOutreachResult(null);
    try {
      const r = await fetch(`${API_URL}/api/school-finder/geo-map/outreach/launch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          state: outreachState,
          video_job_id: outreachVideoId || null,
          only_low_coverage: outreachLowCoverage,
          schedule_mode: outreachSchedule,
          caption_language: language,
          confirm: true,
        }),
      });
      const j = await r.json();
      setOutreachResult(j.success ? { ok: true, ...j } : { ok: false, detail: j.detail || 'Launch failed' });
    } catch (e) {
      setOutreachResult({ ok: false, detail: e.message });
    }
    setOutreachLaunching(false);
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading map…</div>;
  if (!stats || !geo) return <div className="p-6 text-sm text-red-600">Failed to load map data</div>;

  return (
    <div className="space-y-4" data-testid="india-geo-map-panel">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-700 via-teal-700 to-cyan-700 text-white rounded-2xl p-5">
        <h2 className="text-xl font-black flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
          🗺 {language === 'hi' ? 'India School Distribution Map' : 'India School Distribution Map'}
        </h2>
        <p className="text-sm text-white/90 mt-1">
          {language === 'hi'
            ? `${stats.total_schools.toLocaleString('en-IN')} schools · ${stats.states_covered} states/UTs · ${stats.total_districts.toLocaleString('en-IN')} districts। हर state पर hover करें।`
            : `${stats.total_schools.toLocaleString('en-IN')} schools across ${stats.states_covered} states/UTs · ${stats.total_districts.toLocaleString('en-IN')} districts. Hover any state.`}
        </p>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="geo-map-kpis">
        <KpiTile label={language === 'hi' ? 'कुल Schools' : 'Total Schools'} value={stats.total_schools} color="emerald" />
        <KpiTile label={language === 'hi' ? 'WhatsApp Contacts' : 'WhatsApp Contacts'} value={stats.total_with_contact} color="cyan" />
        <KpiTile label={language === 'hi' ? 'Districts' : 'Districts'} value={stats.total_districts} color="teal" />
        <KpiTile label={language === 'hi' ? 'States/UTs' : 'States/UTs'} value={stats.states_covered} color="indigo" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Map */}
        <div className="lg:col-span-2 bg-white rounded-2xl border overflow-hidden" data-testid="india-leaflet-map">
          <div style={{ height: '580px', width: '100%' }}>
            <MapContainer
              center={[22.0, 80.0]}
              zoom={4}
              style={{ height: '100%', width: '100%', background: '#f0fdfa' }}
              scrollWheelZoom={false}
              data-testid="leaflet-map-container"
            >
              <TileLayer
                attribution=''
                url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
              />
              <GeoJSON data={geo} style={styleFor} onEachFeature={onEachFeature} />
            </MapContainer>
          </div>

          {/* Legend */}
          <div className="px-3 py-2 border-t bg-slate-50 flex items-center justify-between flex-wrap gap-2 text-[11px]">
            <span className="font-semibold text-slate-700">
              {language === 'hi' ? 'Schools (% of peak)' : 'Schools (% of peak)'}:
            </span>
            <div className="flex items-center gap-2">
              {LEGEND_BUCKETS.map((b) => (
                <div key={b.label} className="flex items-center gap-1">
                  <span className="w-4 h-3 rounded" style={{ background: b.color }} />
                  <span>{b.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Panel: Hover info OR drilldown OR top-states list */}
        <div className="space-y-3">
          {hoveredState && (
            <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-3" data-testid="hover-card">
              <p className="text-[10px] uppercase font-bold text-emerald-700 tracking-wider">Hovered State</p>
              <p className="text-lg font-black text-emerald-900 mt-1" style={{ fontFamily: 'Outfit' }}>{hoveredState.name}</p>
              <div className="text-xs space-y-0.5 mt-2 text-emerald-900">
                <p><strong>{(hoveredState.schools || 0).toLocaleString('en-IN')}</strong> schools</p>
                {hoveredState.districts != null && <p>{hoveredState.districts} districts</p>}
                {hoveredState.with_contact != null && <p>{hoveredState.with_contact} with WhatsApp ({hoveredState.contact_pct}%)</p>}
              </div>
              <p className="text-[10px] text-emerald-700 mt-2 italic">{language === 'hi' ? '👆 Click for district details' : '👆 Click for district details'}</p>
            </div>
          )}

          {drilldown ? (
            <div className="bg-white rounded-xl border p-3" data-testid="drilldown-panel">
              <div className="flex items-center justify-between">
                <h4 className="font-bold text-sm" style={{ fontFamily: 'Outfit' }}>📍 {drilldown.state}</h4>
                <button onClick={() => setDrilldown(null)} className="text-slate-400 text-lg leading-none" data-testid="drilldown-close-btn">×</button>
              </div>
              {drillLoading ? (
                <p className="text-xs text-slate-500 mt-2">Loading districts…</p>
              ) : drilldown.districts.length === 0 ? (
                <p className="text-xs text-slate-500 mt-2">{language === 'hi' ? 'कोई district data नहीं' : 'No district data'}</p>
              ) : (
                <>
                  <p className="text-[11px] text-slate-500 mt-1">
                    {language === 'hi' ? `${drilldown.total.toLocaleString('en-IN')} schools · top ${drilldown.districts.length} districts` : `${drilldown.total.toLocaleString('en-IN')} schools · top ${drilldown.districts.length} districts`}
                  </p>
                  <button
                    onClick={() => openOutreach(drilldown.state)}
                    className="w-full mt-2 px-3 py-2 bg-gradient-to-r from-rose-600 to-orange-600 text-white text-xs font-black rounded-lg shadow hover:shadow-lg transition"
                    data-testid="auto-launch-outreach-btn"
                  >
                    🚀 {language === 'hi' ? `Auto-Launch Outreach in ${drilldown.state}` : `Auto-Launch Outreach in ${drilldown.state}`}
                  </button>
                  <div className="space-y-1 mt-2 max-h-80 overflow-y-auto">
                    {drilldown.districts.map((d, i) => {
                      const pct = drilldown.max ? d.schools / drilldown.max : 0;
                      return (
                        <div key={d.district} className="text-xs" data-testid={`drilldown-district-${i}`}>
                          <div className="flex justify-between">
                            <span className="font-semibold">{i + 1}. {d.district}</span>
                            <span className="text-slate-500">{d.schools.toLocaleString('en-IN')}</span>
                          </div>
                          <div className="bg-emerald-100 rounded h-1.5 mt-0.5">
                            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded h-1.5" style={{ width: `${Math.max(pct * 100, 2)}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl border p-3" data-testid="top-states-list">
              <h4 className="font-bold text-sm mb-2" style={{ fontFamily: 'Outfit' }}>🏆 {language === 'hi' ? 'Top 10 States by Schools' : 'Top 10 States by Schools'}</h4>
              <div className="space-y-1">
                {stats.items.slice(0, 10).map((s, i) => {
                  const pct = stats.max_schools ? s.schools / stats.max_schools : 0;
                  return (
                    <button
                      key={s.state}
                      onClick={() => loadDrilldown(s.state)}
                      className="w-full text-left text-xs hover:bg-emerald-50 rounded p-1.5 transition"
                      data-testid={`top-state-${i}`}
                    >
                      <div className="flex justify-between">
                        <span className="font-semibold truncate">{i + 1}. {s.state}</span>
                        <span className="text-slate-500">{s.schools.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="bg-emerald-100 rounded h-1.5 mt-0.5">
                        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded h-1.5" style={{ width: `${Math.max(pct * 100, 2)}%` }} />
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-400 italic mt-2">
                {language === 'hi' ? '👆 किसी state पर click करें districts देखने के लिए' : '👆 Click any state for district drill-down'}
              </p>
            </div>
          )}

          {/* Under-served states call-out */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="underserved-callout">
            <p className="text-[10px] font-bold text-amber-700 uppercase">⚡ {language === 'hi' ? 'Outreach Opportunity' : 'Outreach Opportunity'}</p>
            <p className="text-xs text-amber-900 mt-1">
              {language === 'hi'
                ? `${stats.items.filter((s) => s.contact_pct < 5).length} states में 5% से कम schools के पास WhatsApp है। Founding-50 outreach के लिए ये priority targets हैं।`
                : `${stats.items.filter((s) => s.contact_pct < 5).length} states have <5% WhatsApp coverage — priority targets for founding-50 outreach.`}
            </p>
          </div>
        </div>
      </div>

      {/* 🚀 Auto-Launch Outreach Modal */}
      {outreachState && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4" style={{ zIndex: 9999 }} onClick={() => !outreachLaunching && setOutreachState(null)} data-testid="outreach-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-rose-600 via-orange-600 to-amber-600 text-white p-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-black flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  🚀 {language === 'hi' ? `Auto-Launch Outreach` : 'Auto-Launch Outreach'}
                </h3>
                <button onClick={() => !outreachLaunching && setOutreachState(null)} disabled={outreachLaunching} className="text-white text-2xl leading-none disabled:opacity-50" data-testid="outreach-close-btn">×</button>
              </div>
              <p className="text-xs text-white/90 mt-1">{outreachState}</p>
            </div>
            <div className="p-4 space-y-3">
              {/* Preview tile */}
              {outreachPreview ? (
                <div className={`rounded-xl p-3 border ${outreachPreview.eligible ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="outreach-preview-tile">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-[9px] uppercase text-slate-500 font-semibold">Recipients</p>
                      <p className="text-xl font-black text-slate-900" style={{ fontFamily: 'Outfit' }}>{(outreachPreview.recipients_count || 0).toLocaleString('en-IN')}</p>
                    </div>
                    <div>
                      <p className="text-[9px] uppercase text-slate-500 font-semibold">Batches (×50)</p>
                      <p className="text-xl font-black text-slate-900" style={{ fontFamily: 'Outfit' }}>{outreachPreview.batches_50}</p>
                    </div>
                    <div>
                      <p className="text-[9px] uppercase text-slate-500 font-semibold">Coverage</p>
                      <p className="text-xl font-black text-slate-900" style={{ fontFamily: 'Outfit' }}>{outreachPreview.coverage_pct}%</p>
                    </div>
                  </div>
                  {!outreachPreview.eligible && (
                    <p className="text-[11px] text-amber-800 mt-2">⚠ {language === 'hi' ? 'Coverage 5% से ऊपर है — only_low_coverage filter हटा दें।' : 'Coverage above 5% — disable the low-coverage filter.'}</p>
                  )}
                  {outreachPreview.capped_at && (
                    <p className="text-[11px] text-orange-800 mt-1">⚠ {language === 'hi' ? `Capped at ${outreachPreview.capped_at} recipients (safety limit)` : `Capped at ${outreachPreview.capped_at}`}</p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-500">Loading preview…</p>
              )}

              {/* Video selector */}
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1">🎬 {language === 'hi' ? 'कौन सा video भेजना है?' : 'Pick a video'}</p>
                <select value={outreachVideoId} onChange={(e) => setOutreachVideoId(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="outreach-video-select" disabled={outreachLaunching}>
                  <option value="">— {language === 'hi' ? 'Latest completed video (auto)' : 'Latest completed video (auto)'} —</option>
                  {outreachVideos.map((j) => (
                    <option key={j.id} value={j.id}>{j.title || j.id.slice(-12)} ({j.showcase_template_id || 'custom'})</option>
                  ))}
                </select>
              </div>

              {/* Schedule mode */}
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1">⏰ {language === 'hi' ? 'Schedule Mode' : 'Schedule Mode'}</p>
                <div className="grid grid-cols-3 gap-1" data-testid="outreach-schedule-modes">
                  {[
                    { id: 'now', label: language === 'hi' ? '🚀 Now (~1 min)' : '🚀 Now (~1 min)' },
                    { id: 'best_hour', label: language === 'hi' ? '⏰ Best Hour' : '⏰ Best Hour' },
                    { id: 'stagger', label: language === 'hi' ? '🗓 Daily Stagger' : '🗓 Daily Stagger' },
                  ].map((m) => (
                    <button
                      key={m.id}
                      onClick={() => setOutreachSchedule(m.id)}
                      disabled={outreachLaunching}
                      className={`text-xs font-semibold rounded-lg border px-2 py-2 transition ${outreachSchedule === m.id ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
                      data-testid={`outreach-mode-${m.id}`}
                    >{m.label}</button>
                  ))}
                </div>
              </div>

              {/* Low coverage filter */}
              <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="outreach-low-coverage-toggle">
                <input
                  type="checkbox"
                  checked={outreachLowCoverage}
                  onChange={(e) => { setOutreachLowCoverage(e.target.checked); refreshOutreachPreview(e.target.checked); }}
                  disabled={outreachLaunching}
                  className="rounded"
                />
                <span>{language === 'hi' ? 'सिर्फ <5% WA-coverage states को include करें' : 'Only proceed if state has <5% WhatsApp coverage'}</span>
              </label>

              {/* Result */}
              {outreachResult && (
                <div className={`text-sm px-3 py-2 rounded ${outreachResult.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`} data-testid="outreach-result">
                  {outreachResult.ok
                    ? `✅ ${outreachResult.recipients_count.toLocaleString('en-IN')} recipients · ${outreachResult.batches_created} batches · next fire @ ${new Date(outreachResult.next_fire_at).toLocaleString()}`
                    : `❌ ${outreachResult.detail}`}
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-2 pt-3 border-t">
                <button onClick={() => setOutreachState(null)} disabled={outreachLaunching} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-semibold disabled:opacity-50" data-testid="outreach-cancel-btn">
                  {language === 'hi' ? 'बंद करें' : 'Cancel'}
                </button>
                <button
                  onClick={launchOutreach}
                  disabled={outreachLaunching || !outreachPreview || !outreachPreview.eligible || outreachPreview.recipients_count === 0}
                  className="flex-1 py-2 bg-gradient-to-r from-rose-600 to-orange-600 text-white font-bold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="outreach-confirm-btn"
                >
                  {outreachLaunching
                    ? (language === 'hi' ? 'Launching…' : 'Launching…')
                    : (language === 'hi'
                        ? `🚀 Launch Outreach${outreachPreview ? ` (${outreachPreview.recipients_count})` : ''}`
                        : `🚀 Launch Outreach${outreachPreview ? ` (${outreachPreview.recipients_count})` : ''}`)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiTile({ label, value, color = 'emerald' }) {
  const map = {
    emerald: 'from-emerald-500 to-teal-600',
    cyan: 'from-cyan-500 to-blue-600',
    teal: 'from-teal-500 to-emerald-600',
    indigo: 'from-indigo-500 to-purple-600',
  };
  return (
    <div className={`bg-gradient-to-br ${map[color] || map.emerald} text-white rounded-xl p-3`}>
      <p className="text-[10px] uppercase tracking-wider text-white/80 font-semibold">{label}</p>
      <p className="text-2xl font-black mt-1" style={{ fontFamily: 'Outfit' }}>{(value || 0).toLocaleString('en-IN')}</p>
    </div>
  );
}
